import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";
import { useAuth } from "./AuthContext";
import MandatoryTwoFactorSetup from "./MandatoryTwoFactorSetup";
import { Loading } from "../components/Loading";

/**
 * Wrap protected routes. Redirects to /login while preserving the intended
 * destination so the user lands back where they tried to go after sign-in.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { state } = useAuth();
  const location = useLocation();

  if (state.status === "loading") {
    return <div style={{ padding: 32 }}><Loading label={t("common.loading", "Loading…")} /></div>;
  }
  if (state.status === "anonymous") {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  // Grace window elapsed without 2FA: the token is gated server-side to the enrolment endpoints,
  // so block the whole app behind the forced-setup screen until they enrol (matches the BE gate).
  if (state.twoFactor?.setupRequired) {
    return <MandatoryTwoFactorSetup />;
  }
  return <>{children}</>;
}

export function RequirePermission({
  permission,
  anyOf,
  children,
}: {
  permission?: string;
  anyOf?: string[];
  children: ReactNode;
}) {
  const { state, hasPermission } = useAuth();
  if (state.status === "loading") return null;

  const hasRequiredPermission = anyOf
    ? anyOf.some((p) => hasPermission(p))
    : permission
      ? hasPermission(permission)
      : false;

  if (state.status === "anonymous" || !hasRequiredPermission) {
    return <Navigate to="/forbidden" replace />;
  }
  return <>{children}</>;
}

/** Gate a route by role union (e.g. CUST_MANAGER | CUST_PROCUREMENT | CUST_SERVICE). */
export function RequireAnyRole({
  roles,
  children,
}: {
  roles: string[];
  children: ReactNode;
}) {
  const { state, hasRole } = useAuth();
  if (state.status === "loading") return null;
  if (state.status === "anonymous") {
    return <Navigate to="/login" replace />;
  }
  if (!roles.some((r) => hasRole(r))) {
    return <Navigate to="/forbidden" replace />;
  }
  return <>{children}</>;
}
